import * as React from 'react';
import * as _ from 'lodash';
import { css } from 'aphrodite';
import { connect } from 'react-redux';
import { DataShape, Field, FormProps, reduxForm } from 'redux-form';
import { hashHistory } from 'react-router';

import { inputMasks } from 'ui-common';

import store from '../../store';
import { clearOnboardingFormError } from 'login-actions/onboardingActions';
import * as onboardingForm from 'login-utils/onboardingForm';
import * as formUtils from 'login-utils/formUtils';
import ContactSVG from 'login-components/Common/svg/ContactSVG';
import FinanceSVG from 'login-components/Common/svg/FinanceSVG';
import HouseSVG from 'login-components/Common/svg/HouseSVG';
import ErrorMessage from 'login-components/Common/Notifications/ErrorMessage/ErrorMessage';
import InputMap from 'login-components/Common/Inputs/InputMap';
import LoanSVG from 'login-components/Common/svg/LoanSVG';

import * as featureFlags from 'login-utils/featureFlags';
import PaneFormHeader, { PaneFormHeaderProps } from 'login-components/Common/PaneLayout/PaneFormHeader';
import PrimaryButton from 'login-components/Common/Buttons/PrimaryButton';
import States from 'login-constants/states';
import { getWindow } from 'login-utils/globalWrappers';
import { paneFormStyles } from 'login-components/Common/PaneLayout/paneStyles';

const sharedConstants = getWindow().SHARED_CONSTANTS;

interface OnboardingFormSection {
  header: PaneFormHeaderProps;
  inputs: FormInput[];
}

const formSections: OnboardingFormSection[] = [
  {
    header: {
      iconComponent: HouseSVG,
      text: 'Property Info',
    },
    inputs: [
      {
        ...InputMap.SelectInput,
        identifier: 'type of home',
        hidePlaceholder: true,
        name: onboardingForm.FIELDS.propertyType,
        label: 'What type of home is it?',
        choices: sharedConstants.PropertyTypes,
      },
      {
        ...InputMap.SelectInput,
        identifier: 'use of home',
        hidePlaceholder: true,
        name: onboardingForm.FIELDS.propertyUsageType,
        label: 'How will you use this home?',
        choices: sharedConstants.PropertyUsageTypes,
      },
      {
        ...InputMap.CountySelector,
        identifier: 'county',
        hidePlaceholder: true,
        name: onboardingForm.FIELDS.propertyCounty,
        label: 'Where are you planning to buy a home? (County/Municipality)',
      },
    ],
  },
  {
    header: {
      iconComponent: LoanSVG,
      text: 'Loan Info',
    },
    inputs: [
      {
        ...InputMap.MoneyInput,
        identifier: 'purchase price',
        hidePlaceholder: true,
        name: onboardingForm.FIELDS.purchasePrice,
        label: `What's the purchase price?`,
        validate: formUtils.generateValidatePositiveInteger('Purchase price must be greater than or equal to 0.'),
      },
      {
        ...InputMap.MoneyInput,
        identifier: 'down payment',
        hidePlaceholder: true,
        name: onboardingForm.FIELDS.cashDownPayment,
        label: `What's your down payment?`,
        validate: formUtils.cashDownPaymentValidator,
      },
    ],
  },
  {
    header: {
      iconComponent: FinanceSVG,
      text: 'Financial Info',
    },
    inputs: [
      {
        ...InputMap.SelectInput,
        identifier: 'employment type',
        hidePlaceholder: true,
        name: 'employmentType',
        label: 'Employment type',
        choices: sharedConstants.EmploymentTypeDefinitions,
      },
      {
        ...InputMap.MoneyInput,
        identifier: 'annual income',
        hidePlaceholder: true,
        name: 'annualIncome',
        label: 'Your annual income',
        validate: formUtils.generateValidatePositiveInteger('Annual income must be greater than or equal to 0.'),
      },
      {
        ...InputMap.DateInput,
        identifier: 'start date',
        name: 'employmentStartDate',
        label: 'Employment start date',
        validate: formUtils.validateDate,
      },
      {
        ...InputMap.SelectInput,
        identifier: 'estimated credit score',
        hidePlaceholder: true,
        name: onboardingForm.FIELDS.estimatedCreditScore,
        label: 'Estimated credit score',
        choices: onboardingForm.CREDIT_SCORE_CHOICES,
      },
    ],
  },
  {
    header: {
      iconComponent: ContactSVG,
      text: 'Contact Info',
    },
    inputs: [
      {
        ...InputMap.SimpleTextInput,
        identifier: 'first name',
        hidePlaceholder: true,
        name: 'firstName',
        label: 'First name',
        validate: formUtils.generateValidateNoDisallowedCharacters(['.'], 'Please enter your name without periods.'),
      },
      {
        ...InputMap.SimpleTextInput,
        identifier: 'last name',
        hidePlaceholder: true,
        name: 'lastName',
        label: 'Last name',
        validate: formUtils.generateValidateNoDisallowedCharacters(['.'], 'Please enter your name without periods.'),
      },
      {
        ...InputMap.EmailInput,
        identifier: 'email',
        hidePlaceholder: true,
        name: 'email',
        label: 'Email',
        hideIcon: true,
      },
      {
        ...InputMap.PhoneInput,
        identifier: 'phone number',
        name: 'homePhone',
        label: 'Phone number',
        normalize: inputMasks.PHONE.normalize,
        mask: inputMasks.PHONE.maskedInputConfig as any,
      },
    ],
  },
];

const termsInput: FormInput = {
  ...InputMap.RequirementInput,
  acceptedTermsTypes: ['termsOfUse', 'privacyPolicy', 'eConsent'],
  identifier: 'terms of use and e-consent',
  name: 'acceptTermsAndEconsentGiven',
};

export const allInputs = featureFlags.isEnabled('truncatedEConsent20180817')
  ? _.concat([termsInput], _.flatten(_.map(formSections, definition => definition.inputs)))
  : _.flatten(_.map(formSections, definition => definition.inputs));

interface OnboardingFormProps {
  error: string;
}

export class OnboardingForm extends React.Component<
  OnboardingFormProps & ReactRouter.RouteComponentProps<{}, {}> & FormProps<DataShape, {}, ReduxState>
> {
  goToMilestones = () => {
    const { location } = this.props;
    const search = location!.search || '';
    hashHistory.push(`${States.MILESTONES}${search}`);
  };

  goToUpfrontRates = () => {
    const { location } = this.props;
    const search = location!.search || '';
    hashHistory.push(`${States.UPFRONT_RATES}${search}`);
  };

  render() {
    const { error, handleSubmit } = this.props;
    const onSubmit = handleSubmit!(
      featureFlags.isEnabled('upfrontRatesInLogin20190117') ? this.goToUpfrontRates : this.goToMilestones,
    );

    return (
      <div className="OnboardingForm">
        {error && (
          <div className={css(paneFormStyles.errorWrapper)}>
            <ErrorMessage errorText={error} />
          </div>
        )}
        <form className={css(paneFormStyles.wrapper)} noValidate onSubmit={onSubmit}>
          {/* We wrap all the form sections in a div in order to select on the last one with :last-child and
              stop it from adding extra padding */}
          <div>
            {formSections.map(definition => (
              <div className={css(paneFormStyles.sectionWrapper)} key={definition.header.text}>
                <PaneFormHeader {...definition.header} />
                <div className={css(paneFormStyles.inputsWrapper)}>
                  {definition.inputs.map(input => (
                    <div className={css(paneFormStyles.fieldWrapper)} key={input.name}>
                      <Field {...input} />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <div className={css(paneFormStyles.buttonPadding)}>
            {featureFlags.isEnabled('truncatedEConsent20180817') && (
              <div className={css(paneFormStyles.fieldWrapper)} key={termsInput.name}>
                <Field {...termsInput} />
              </div>
            )}
            <div className={css(paneFormStyles.buttonDisplay)}>
              <PrimaryButton isWide={true} text="Continue" fontFamily="Roboto" data-it="OnboardingForm-continue" />
            </div>
          </div>
        </form>
      </div>
    );
  }
}

const ConnectedOnboardingForm = connect(state => {
  return {
    error: _.get(state, 'onboarding.onboardingFormError') as string,
  };
})(OnboardingForm);

const FormConnectedOnboardingForm = reduxForm<any, any>({
  ...onboardingForm.FORM_CONFIG,
  validate: (values: DataShape) => formUtils.validate(allInputs, values) as any,
  onSubmitSuccess: () => {
    store.dispatch(clearOnboardingFormError());
  },
})(ConnectedOnboardingForm);

export default FormConnectedOnboardingForm;
